import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CollabDiscussion from "@/components/CollabDiscussion";
import { labelCreatorType } from "@/lib/roleCategories";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { VerifiedBadge } from "@/components/Badges";
import { MapPin, Calendar, Euro, ArrowLeft, Crown, Sparkles, MessageCircle, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function CollaborationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collab, setCollab] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [quota, setQuota] = useState(null);
  const [isParticipant, setIsParticipant] = useState(false);
  const initialTab = searchParams.get("tab") === "discussion" ? "discussion" : "details";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    setNotFound(false);
    api.get(`/collaborations/${id}`)
      .then(({ data }) => setCollab(data))
      .catch(() => setNotFound(true));
  }, [id]);

  // Detect if the current user is a participant (so we can show the Discussion tab).
  // Anyone who can fetch /participants is a participant — endpoint returns 403 otherwise.
  useEffect(() => {
    if (!user) { setIsParticipant(false); return; }
    api.get(`/collaborations/${id}/participants`)
      .then(() => setIsParticipant(true))
      .catch(() => setIsParticipant(false));
  }, [id, user]);

  // Keep ?tab=… in sync with the active tab for shareable links / notifications deep-link.
  useEffect(() => {
    const current = searchParams.get("tab");
    if (tab === "discussion" && current !== "discussion") {
      const next = new URLSearchParams(searchParams); next.set("tab", "discussion"); setSearchParams(next, { replace: true });
    } else if (tab === "details" && current === "discussion") {
      const next = new URLSearchParams(searchParams); next.delete("tab"); setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (user?.role === "talent") {
      api.get("/applications/quota").then(({ data }) => setQuota(data)).catch(() => {});
    }
  }, [user]);

  const handleApply = async () => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (user.role !== "talent") {
      toast.error("Seuls les talents peuvent postuler");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/collaborations/${id}/apply`, { message });
      toast.success("Candidature envoyée, votre portfolio a été transmis automatiquement.");
      setOpen(false);
      navigate("/talent/applications");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="collab-not-found">
          <p className="caption-label text-[#A26F3E]">Collaboration introuvable</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-2">Cette collaboration n'est plus disponible</h1>
          <p className="text-[#666] mt-3">Elle a peut-être été clôturée ou supprimée.</p>
          <Button onClick={() => navigate("/collaborations")} className="mt-8 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="back-to-collabs-btn">
            Voir toutes les collaborations
          </Button>
        </div>
      </AppShell>
    );
  }

  if (!collab) {
    return (
      <AppShell>
        <div className="max-w-4xl mx-auto px-6 py-24 text-center text-[#8B8680]">Chargement…</div>
      </AppShell>
    );
  }

  const canApply = !user || user.role === "talent";
  // While quota is still loading for a talent, treat the button as not-yet-ready
  // to avoid the brief "Postuler" flicker on premium-only / quota-saturated cases.
  const quotaLoading = user?.role === "talent" && quota === null;
  const isPremiumOnlyBlocked = collab.is_premium_only && user?.role === "talent" && (!quota || !quota.is_premium);
  const isQuotaReached = !collab.is_premium_only && user?.role === "talent" && quota && !quota.is_premium && quota.remaining === 0;

  return (
    <AppShell>
      <div data-testid="collab-detail-page">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <p className="caption-label text-[#A26F3E] mb-3">{collab.project_type}</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mb-6 leading-tight">
          {collab.title}
        </h1>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#FAF3EA] rounded-full p-1 h-auto mb-8" data-testid="collab-tabs">
            <TabsTrigger
              value="details"
              className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-white data-[state=active]:text-[#1A1A1A] data-[state=active]:shadow-sm text-[#6E4B2A]"
              data-testid="collab-tab-details"
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Détails
            </TabsTrigger>
            {isParticipant && (
              <TabsTrigger
                value="discussion"
                className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-white data-[state=active]:text-[#1A1A1A] data-[state=active]:shadow-sm text-[#6E4B2A]"
                data-testid="collab-tab-discussion"
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Discussion
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="details" className="mt-0 space-y-0">
        {/* Hero image + gallery */}
        {(collab.cover_image || (collab.gallery_images?.length > 0)) && (
          <div className="mb-10">
            {collab.cover_image && (
              <div className="rounded-3xl overflow-hidden aspect-[21/9] bg-[#FAF3EA] shadow-[0_20px_60px_-20px_rgba(110,75,42,0.15)]">
                <img src={`${API}/img/${collab.cover_image}?w=1600&q=86`} alt={collab.title} className="w-full h-full object-cover" data-testid="collab-cover" />
              </div>
            )}
            {collab.image_caption && (
              <p className="mt-4 text-center italic text-[#6E4B2A] font-serif text-lg">« {collab.image_caption} »</p>
            )}
            {collab.gallery_images?.length > 0 && (
              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                {collab.gallery_images.filter((p) => p !== collab.cover_image).map((p) => (
                  <div key={p} className="rounded-2xl overflow-hidden aspect-square bg-[#FAF3EA]">
                    <img src={`${API}/img/${p}?w=1600&q=86`} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-6 text-sm text-[#666] mb-10">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" /> {collab.location}
          </div>
          {collab.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(collab.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </div>
          )}
          {collab.paid && (
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-[#E7F5EC] text-[#2E7D32] border border-[#BFE4C5] font-semibold">
              <Euro className="w-3 h-3" /> Rémunéré
            </span>
          )}
        </div>

        <div className="card-lux p-8 mb-8">
          <p className="text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">
            {collab.description}
          </p>
        </div>

        {(collab.is_premium_only || isQuotaReached) && (
          <div className="rounded-2xl border border-[#E6C29A] bg-gradient-to-br from-[#FAF3EA] to-white p-6 mb-8 flex items-center gap-4" data-testid="premium-banner">
            <div className="w-11 h-11 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-[#D4A574]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="caption-label text-[#A26F3E]">Helencia Premium</p>
              <p className="font-serif text-lg text-[#1A1A1A] mt-0.5 leading-tight">
                {collab.is_premium_only
                  ? "Cette collaboration est réservée aux talents Premium."
                  : `Vous avez atteint votre quota gratuit (${quota?.limit || 3} candidatures/jour).`}
              </p>
              <p className="text-xs text-[#6E4B2A] mt-1">Passez Premium pour candidatures illimitées et accès prioritaire.</p>
            </div>
            {user?.role === "talent" && (
              <Link to="/talent/premium">
                <Button className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white" data-testid="premium-cta-banner">
                  <Sparkles className="w-3.5 h-3.5 mr-2" /> Passer Premium
                </Button>
              </Link>
            )}
          </div>
        )}

        <div className="card-lux p-6 mb-8 flex items-center justify-between">
          <div>
            <p className="caption-label mb-1">Proposé par</p>
            <div className="flex items-center gap-2">
              <p className="font-serif text-xl text-[#1A1A1A]">{collab.pro_name}</p>
              {collab.pro_verified && <VerifiedBadge />}
            </div>
            {collab.pro_type && <p className="text-xs text-[#666] mt-1">{labelCreatorType(collab.pro_type)}</p>}
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-8 disabled:opacity-50"
                disabled={!canApply || quotaLoading || isPremiumOnlyBlocked || isQuotaReached}
                data-testid="apply-btn"
              >
                {!canApply
                  ? "Réservé aux talents"
                  : quotaLoading
                    ? "…"
                    : isPremiumOnlyBlocked
                      ? "Réservée aux Premium"
                      : isQuotaReached
                        ? "Quota atteint"
                        : "Postuler"}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Postuler à cette collaboration</DialogTitle>
                <DialogDescription>
                  Votre portfolio complet sera automatiquement envoyé au Créateur.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label className="caption-label">Message (optionnel)</Label>
                <Textarea
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Présentez-vous brièvement…"
                  className="rounded-xl"
                  data-testid="apply-message-input"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={handleApply}
                  disabled={submitting}
                  className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white"
                  data-testid="apply-submit-btn"
                >
                  {submitting ? "Envoi…" : "Envoyer ma candidature"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
          </TabsContent>

          {isParticipant && (
            <TabsContent value="discussion" className="mt-0" data-testid="collab-discussion-content">
              <CollabDiscussion collaborationId={id} />
            </TabsContent>
          )}
        </Tabs>
      </div>
      </div>
    </AppShell>
  );
}
